import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";

interface DishForm4Props {
  onNext: (data: DishForm4Data) => void;
  onPrevious: () => void;
  onChange?: (data: Partial<DishForm4Data>) => void;
  initialData?: DishForm4Data;
}

export interface DishForm4Data {
  isAvailable: boolean;
  preparationTime: string;
  serves: string;
  calories: string;
  isRecommended: boolean;
  maxQuantityPerOrder: number | string;
  sortOrder: string;
}

const DishForm4: React.FC<DishForm4Props> = ({ onNext, onPrevious, onChange, initialData }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<DishForm4Data>({
    isAvailable: initialData?.isAvailable ?? false,
    preparationTime: initialData?.preparationTime ?? "",
    serves: initialData?.serves ?? "",
    calories: initialData?.calories ?? "",
    isRecommended: initialData?.isRecommended ?? false,
    sortOrder: initialData?.sortOrder ?? "",
    maxQuantityPerOrder: initialData?.maxQuantityPerOrder ?? "",
  });

  console.log("maxQuantityPerOrder",formData.maxQuantityPerOrder)

  const [error, setError] = useState("");

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        isAvailable: initialData.isAvailable ?? false,
        preparationTime: initialData.preparationTime ?? "",
        serves: initialData.serves ?? "",
        calories: initialData.calories ?? "",
        isRecommended: initialData.isRecommended ?? false,
        sortOrder: initialData.sortOrder ?? "",
        maxQuantityPerOrder: initialData.maxQuantityPerOrder ?? "",
      });
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    
    // For numeric fields, allow only numbers or empty string
    const numericFields = ['preparationTime', 'calories', 'sortOrder', 'maxQuantityPerOrder'];
    if (numericFields.includes(name)) {
      // Allow empty string or valid numbers only
      if (value !== '' && !/^\d+$/.test(value)) {
        return; // Don't update if it's not a valid number
      }
    }
    
    const updatedData = { [name]: value };
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
    setError("");
    
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange(updatedData as Partial<DishForm4Data>);
    }
  };

  const handleToggle = (field: "isAvailable" | "isRecommended") => {
    const newValue = !formData[field];
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ [field]: newValue } as Partial<DishForm4Data>);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.preparationTime.trim()) {
      setError("Preparation time is required");
      return;
    }
    if (parseFloat(formData.preparationTime) <= 0) {
      setError("Preparation time must be greater than 0");
      return;
    }
    
    // Convert maxQuantityPerOrder to number and validate
    const maxQty = typeof formData.maxQuantityPerOrder === 'string' 
      ? parseInt(formData.maxQuantityPerOrder, 10) 
      : formData.maxQuantityPerOrder;
    
    if (!maxQty || maxQty < 1) {
      setError("Max quantity per order must be at least 1");
      return;
    }

    // Submit with maxQuantityPerOrder as a number
    onNext({
      ...formData,
      maxQuantityPerOrder: maxQty
    });
  };

  return (
    <div className="px-3 sm:px-4 md:px-6">    
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Is Available */}
        <div className="flex items-center justify-between">
          <label className={`font-Urbanist text-[14px] font-medium ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Is Available
          </label>
          <button
            type="button"
            onClick={() => handleToggle("isAvailable")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.isAvailable ? "bg-[#FF8A00]" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.isAvailable ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Preparation Time */}
        <div>
          <label className={`block font-Urbanist text-[14px] font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Preparation time
          </label>
          <input
            type="text"
            inputMode="numeric"
            name="preparationTime"
            value={formData.preparationTime}
            onChange={handleChange}
            placeholder="In minutes"
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* Serves */}
        <div>
          <label className={`block font-Urbanist text-[14px] font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Serves
          </label>
          <input
            type="text"
            name="serves"
            value={formData.serves}
            onChange={handleChange}
            placeholder="ex. Serves 4+"
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* Calories */}
        <div>
          <label className={`block font-Urbanist text-[14px] font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Calories
          </label>
          <input
            type="text"
            inputMode="numeric"
            name="calories"
            value={formData.calories}
            onChange={handleChange}
            placeholder="in Kcal"
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* Is Recommended */}
        <div className="flex items-center justify-between">
          <label className={`font-Urbanist text-[14px] font-medium ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Is Recommended
          </label>
          <button
            type="button"
            onClick={() => handleToggle("isRecommended")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.isRecommended ? "bg-[#FF8A00]" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.isRecommended ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Sort Order */}
        <div>
          <label className={`block font-Urbanist text-[14px] font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Sort Order
          </label>
          <input
            type="text"
            inputMode="numeric"
            name="sortOrder"
            value={formData.sortOrder}
            onChange={handleChange}
            placeholder="05"
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>
        {/* Max Quantity Per Order */}
        <div>
          <label className={`block font-Urbanist text-[14px] font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Max Quantity Per Order
          </label>
          <input
            type="text"
            inputMode="numeric"
            name="maxQuantityPerOrder"
            value={formData.maxQuantityPerOrder}
            onChange={handleChange}
            placeholder="05"
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>


        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-sm font-Urbanist">{error}</div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
          <button
            type="button"
            onClick={onPrevious}
            className={`w-full sm:flex-1 border-2 hover:border-gray-400 font-Urbanist text-sm sm:text-[16px] font-semibold py-3 sm:py-3.5 rounded-full transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-[#050505]'
            }`}
          >
            Previous
          </button>
          <button
            type="submit"
            className="w-full sm:flex-1 bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-sm sm:text-[16px] font-semibold py-3 sm:py-3.5 rounded-full transition-colors"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default DishForm4;
